import React, { useEffect, useMemo, useRef, useState } from "react";
import { Tree, Input, Dropdown, Empty } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
const { Search } = Input;

const KEY_DELIMITER = "::";
const extractIdFromKey = (fullKey) =>
  String(fullKey || "").split(KEY_DELIMITER)[0];

export default function CategoryTree({
  data = [],
  onSelect,
  onCreate,
  onDelete,
  onRename,
  selectedId,
}) {
  const [rawTree, setRawTree] = useState([]); // normalized tree: { title: string, type, key, children }
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [autoExpandParent, setAutoExpandParent] = useState(true);
  const [selectedKeys, setSelectedKeys] = useState([]);

  // debounce timer ref
  const debounceRef = useRef(null);
  const DEBOUNCE_MS = 300;

  useEffect(() => {
    if (Array.isArray(data)) {
      setRawTree(formatForTree(data));
    }
  }, [data]);

  // Sort data by ranking (descending) before formatting
  const sortByRanking = (list) => {
    if (!list || !list.length) return list;
    return [...list].sort((a, b) => (b.ranking || 0) - (a.ranking || 0));
  };

  // format incoming categories to a consistent tree shape
  const formatForTree = (list, parentKey = "root") => {
    const sortedList = sortByRanking(list);
    return sortedList.map((item, index) => {
      const thisKey = `${item.id}${KEY_DELIMITER}${parentKey}`;

      return {
        title: item.name,
        key: thisKey,
        type: item.type,
        ranking: item.ranking || 0,
        children: item.children?.length
          ? formatForTree(item.children, thisKey)
          : [],
      };
    });
  };

  // build a flat list of nodes for searching / parent lookup
  const dataList = useMemo(() => {
    const list = [];
    const gen = (nodes) => {
      nodes.forEach((n) => {
        list.push({ key: n.key, title: String(n.title) });
        if (n.children) gen(n.children);
      });
    };
    gen(rawTree);
    return list;
  }, [rawTree]);

  // auto selecting newly added node
  useEffect(() => {
    if (!selectedId) return;

    const foundKey = dataList.find(
      (item) => extractIdFromKey(item.key) === String(selectedId)
    )?.key;

    if (foundKey) {
      setSelectedKeys([foundKey]);
      setExpandedKeys((prev) => [
        ...new Set([...prev, getParentKey(foundKey, rawTree)]),
      ]);
    }
  }, [selectedId, dataList, rawTree]);

  // find parent key given a key, returns undefined if none
  const getParentKey = (key, tree) => {
    let parentKey;
    for (let i = 0; i < tree.length; i += 1) {
      const node = tree[i];
      if (node.children) {
        if (node.children.some((item) => item.key === key)) {
          parentKey = node.key;
        } else {
          const pk = getParentKey(key, node.children);
          if (pk) parentKey = pk;
        }
      }
    }
    return parentKey;
  };

  // search handler (debounced)
  const runSearch = (value) => {
    const q = String(value || "").trim();
    setSearchValue(q);

    if (!q || q.length < 3) {
      // collapse everything and stop auto expanding
      setExpandedKeys([]);
      setAutoExpandParent(false);
      return;
    }

    const lowercaseQ = q.toLowerCase();

    const newExpandedKeys = dataList
      .map((item) => {
        if (String(item.title).toLowerCase().includes(lowercaseQ)) {
          return getParentKey(item.key, rawTree);
        }
        return null;
      })
      .filter((v, i, self) => v && self.indexOf(v) === i);

    setExpandedKeys(newExpandedKeys);
    setAutoExpandParent(true);
  };

  // called on input change; debounce the actual search
  const handleSearchInput = (value) => {
    // clear pending timer
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
    }

    // If user cleared the field (empty), run immediate collapse so nodes don't linger
    if (!value || !String(value).trim()) {
      runSearch("");
      return;
    }

    // otherwise debounce the search
    debounceRef.current = window.setTimeout(() => {
      runSearch(value);
      debounceRef.current = null;
    }, DEBOUNCE_MS);
  };

  // cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // when user manually expands/collapses, stop auto expanding parents
  const onExpand = (newExpandedKeys) => {
    setExpandedKeys(newExpandedKeys);
    setAutoExpandParent(false);
  };

  // transform tree to highlight search matches
  const highlightedTreeData = useMemo(() => {
    const q = String(searchValue || "");

    const loop = (nodes) =>
      nodes.map((item) => {
        const strTitle = String(item.title);
        const index = q ? strTitle.toLowerCase().indexOf(q.toLowerCase()) : -1;

        // Build the menu items dynamically, hide "Create Subcategory" for LEAF nodes
        const menuItems = [
          { label: "✏️ Rename", key: "rename" },
          ...(item.type !== "LEAF"
            ? [{ label: "➕ Create Subcategory", key: "create" }]
            : []),
          {
            label: <span style={{ color: "red" }}>❌ Delete</span>,
            key: "delete",
          },
        ];

        const dropdown = (
          <Dropdown
            trigger={["click"]}
            menu={{
              items: menuItems,
              onClick: ({ key }) => {
                if (key === "create") onCreate(extractIdFromKey(item.key));
                if (key === "rename") onRename(extractIdFromKey(item.key));
                if (key === "delete") onDelete(extractIdFromKey(item.key));
              },
            }}
          >
            <FontAwesomeIcon
              icon="ellipsis-h"
              className="node-action"
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        );

        // highlight search match if applicable
        const titleContent =
          q && index > -1 ? (
            <>
              {strTitle.substring(0, index)}
              <span className="site-tree-search-value">
                {strTitle.slice(index, index + q.length)}
              </span>
              {strTitle.slice(index + q.length)}
            </>
          ) : (
            strTitle
          );

        const titleNode = (
          <span
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <span>{titleContent}</span>
            {dropdown}
          </span>
        );

        // recursively process children
        return {
          title: titleNode,
          key: item.key,
          children: item.children?.length ? loop(item.children) : [],
        };
      });

    return loop(rawTree);
  }, [rawTree, searchValue, onCreate, onDelete, onRename]);

  // drag & drop handler
  const onDrop = (info) => {
    const dropKey = info.node.key;
    const dragKey = info.dragNode.key;
    const dropPos = info.node.pos.split("-");
    const dropPosition =
      info.dropPosition - Number(dropPos[dropPos.length - 1]);

    const dataCopy = JSON.parse(JSON.stringify(rawTree)); // clone to avoid mutation issues
    let dragObj;

    const loop = (list, key, callback) => {
      for (let i = 0; i < list.length; i++) {
        const item = list[i];
        if (item.key === key) {
          return callback(item, i, list);
        }
        if (item.children) {
          loop(item.children, key, callback);
        }
      }
    };

    loop(dataCopy, dragKey, (item, index, arr) => {
      arr.splice(index, 1);
      dragObj = item;
    });

    if (!info.dropToGap) {
      loop(dataCopy, dropKey, (item) => {
        item.children = item.children || [];
        item.children.push(dragObj);
      });
    } else {
      let arr;
      let index;
      loop(dataCopy, dropKey, (item, i, parentArr) => {
        arr = parentArr;
        index = i;
      });

      if (dropPosition === -1) {
        arr.splice(index, 0, dragObj);
      } else {
        arr.splice(index + 1, 0, dragObj);
      }
    }

    setRawTree(dataCopy);
  };

  return (
    <div className="p-3">
      <Search
        placeholder="Search for Category"
        onChange={(e) => handleSearchInput(e.target.value)}
        style={{ marginBottom: 8 }}
        allowClear
      />

      <div
        className="tree-scroll pe-2"
        style={{
          height: "75vh",
          overflow: "auto",
        }}
      >
        {data && data?.length > 0 ? (
          <Tree
            treeData={highlightedTreeData}
            expandedKeys={expandedKeys}
            selectedKeys={selectedKeys}
            onExpand={onExpand}
            autoExpandParent={autoExpandParent}
            onSelect={(keys) => {
              const fullKey = keys[0];

              if (!fullKey) {
                // prevent deselection
                setSelectedKeys((prev) => prev);
                return;
              }

              // force selection to stay
              setSelectedKeys([fullKey]);

              const extractedId = extractIdFromKey(fullKey);
              onSelect(extractedId);
            }}
            draggable
            blockNode
            onDrop={onDrop}
            titleRender={(node) => {
              return <div className="tree-node-wrapper">{node.title}</div>;
            }}
          />
        ) : (
          <div className="h-100 d-flex align-items-center justify-content-center">
            <Empty />
          </div>
        )}
      </div>
    </div>
  );
}
